import { DOCUMENT } from '@angular/common';
import { Directive, Input, ElementRef, HostListener, Renderer2, Inject, OnDestroy, input } from '@angular/core';
import { Router } from '@angular/router';

export type ToolTipPosition = 'top' | 'bottom' | 'left' | 'right';

@Directive({
  selector: '[smlTooltip]',
  standalone: true
})
export class TooltipDirective implements OnDestroy {
  @Input('smlTooltip') tooltipTitle: any = '';
  @Input() placement: ToolTipPosition | '' = 'top';
  @Input() delay = 500;
  @Input() tooltipDisabled:boolean|null = false;
  mode = input<'always' | 'auto'>('auto');
  tooltip: HTMLElement | undefined | null;
  offset = 10;
  isMobile: boolean = false;

  constructor(
    private readonly el: ElementRef,
    private readonly renderer: Renderer2,
    private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.router.events.subscribe(() => {
      if (this.tooltip) { this.hide(); }
    });
  }

  @HostListener('window:touchmove', ['$event']) onTouchMove(event: TouchEvent) {
    // Detect if the user is scrolling on a touch device
    if (this.tooltip) {
      this.hide();
    }
  }

  ngOnDestroy() {
    // Clean up the tooltip when directive is destroyed
    if (this.tooltip) { this.hide(); }
  }

  @HostListener('mouseenter') onMouseEnter() {
    // Check for horizontal overflow (ellipsis)
    const isTextOverflowing = this.el.nativeElement.scrollWidth > this.el.nativeElement.clientWidth;
  
    // Check for vertical overflow as well (for multi-line ellipsis)
    const isVerticalOverflowing = this.el.nativeElement.scrollHeight > (this.el.nativeElement.clientHeight+1);
  
    // If the element is overflowing horizontally or vertically and tooltip is not disabled, show the tooltip
    if (!this.tooltip && !this.tooltipDisabled && (isTextOverflowing || isVerticalOverflowing || this.mode() === 'always') && !this.isMobile) {
      this.show();
    }
  }
  

  @HostListener('touchstart') onTouchStart() {
    // Only show tooltip on touchstart for mobile/tablet
    // Check for horizontal overflow (ellipsis)
    const isTextOverflowing = this.el.nativeElement.scrollWidth > this.el.nativeElement.clientWidth;
  
    // Check for vertical overflow as well (for multi-line ellipsis)
    const isVerticalOverflowing = this.el.nativeElement.scrollHeight > this.el.nativeElement.clientHeight;
  
    // If the element is overflowing horizontally or vertically and tooltip is not disabled, show the tooltip
    if (!this.tooltip && !this.tooltipDisabled && (isTextOverflowing || isVerticalOverflowing || this.mode() === 'always') && !this.isMobile) {
      this.show();
    }
  }

  @HostListener('mouseleave') onMouseLeave() {
    if (!this.isMobile && this.tooltip) {
      this.hide();
    }
  }

  @HostListener('touchend') onTouchEnd() {
    // On touchend, hide the tooltip if it was shown
    setTimeout(() => {
      if (this.tooltip) {
        this.hide();
      }
    }, 300);
  }
  
  @HostListener('mouseup') onMouseUp() {
    // On touchend, hide the tooltip if it was shown
    if (this.tooltip) {
      this.hide();
    }
  }

  show() {
    this.create();
    this.setPosition();
    this.renderer.addClass(this.tooltip, 'smlTooltip-show'); 
  }

  hide() {
    if (this.tooltip) {
      this.renderer.removeClass(this.tooltip, 'smlTooltip-show');
      this.renderer.setStyle(this.tooltip, 'visibility', `hidden`);
      this.renderer.removeChild(this.document.body, this.tooltip);
      this.tooltip = null;
    }
  }

  create() {
    this.tooltip = this.renderer.createElement('span');

    // Use renderer to set innerHTML for HTML content
    this.renderer.setProperty(this.tooltip, 'innerHTML', this.tooltipTitle);

    this.renderer.appendChild(this.document.body, this.tooltip);
    this.renderer.addClass(this.tooltip, 'smlTooltip');
    this.renderer.addClass(this.tooltip, `smlTooltip-${this.placement}`);
    this.renderer.setStyle(this.tooltip, '-webkit-transition', `opacity ${this.delay}ms`);
    this.renderer.setStyle(this.tooltip, '-moz-transition', `opacity ${this.delay}ms`);
    this.renderer.setStyle(this.tooltip, '-o-transition', `opacity ${this.delay}ms`);
    this.renderer.setStyle(this.tooltip, 'transition', `opacity ${this.delay}ms`);
  }

  setPosition(): void {
    const hostPos = this.el.nativeElement.getBoundingClientRect();
    const tooltipPos = this.tooltip?.getBoundingClientRect();
    const scrollPos = window.scrollY || this.document.documentElement.scrollTop || this.document.body.scrollTop || 0;
  
    if (!tooltipPos) return;
  
    let { top, left } = this.calculatePlacementPosition(hostPos, tooltipPos);
  
    if (this.isMobile) {
      ({ top, left } = this.adjustForMobile(top, left, tooltipPos));
    }
  
    this.renderer.setStyle(this.tooltip, 'top', `${top + scrollPos}px`);
    this.renderer.setStyle(this.tooltip, 'left', `${left}px`);
  }

  private calculatePlacementPosition(host: DOMRect, tooltip: DOMRect): { top: number; left: number } {
    switch (this.placement) {
      case 'top':
        return {
          top: host.top - tooltip.height - this.offset,
          left: host.left + (host.width - tooltip.width) / 2,
        };
      case 'bottom':
        return {
          top: host.bottom + this.offset,
          left: host.left + (host.width - tooltip.width) / 2,
        };
      case 'left':
        return {
          top: host.top + (host.height - tooltip.height) / 2,
          left: host.left - tooltip.width - this.offset,
        };
      case 'right':
        return {
          top: host.top + (host.height - tooltip.height) / 2,
          left: host.right + this.offset,
        };
      default:
        return { top: host.top, left: host.left };
    }
  }
  
  private adjustForMobile(top: number, left: number, tooltip: DOMRect): { top: number; left: number } {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
  
    if (top < 0) top = this.offset;
    if (left + tooltip.width > windowWidth) left = windowWidth - tooltip.width - this.offset;
    if (left < 0) left = this.offset;
    if (top + tooltip.height > windowHeight) top = windowHeight - tooltip.height - this.offset;
  
    return { top, left };
  }
  
  
}
